// frontend/src/pages/PaymentSuccessPage.js
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { paymentApi } from '../api/paymentApi';
import { FaCheckCircle, FaArrowRight, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';

function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('Confirming your payment with the bank...');

  useEffect(() => {
    const verify = async () => {
      // Flutterwave appends ?status=successful&tx_ref=...&transaction_id=...
      const transactionId = searchParams.get('transaction_id');
      const txStatus = searchParams.get('status');

      if (!transactionId) {
          // Fallback: If no ID, assume success if user navigated here manually (rare)
          setStatus('success');
          setMessage('Payment completed.');
          return;
      }

      if (txStatus === 'cancelled') {
          setStatus('error');
          setMessage('Payment was cancelled.');
          return;
      }

      try {
          // Call Backend to Force Update DB
          await paymentApi.verifyTransaction(transactionId);
          setStatus('success');
          setMessage('Payment confirmed! Your order is now processing.');
      } catch (err) {
          console.error("Verification failed:", err);
          // Even if verification API fails, if status was successful, we show success
          // The webhook will likely catch it later.
          if (txStatus === 'successful') {
              setStatus('success');
              setMessage('Payment successful! (Verification pending)');
          } else {
              setStatus('error');
              setMessage('Could not verify payment. Please contact support.');
          }
      }
    };

    verify();
  }, [searchParams]);

  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center bg-gray-50 px-4 text-center">
      <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
        
        {status === 'verifying' && (
            <>
                <FaSpinner className="text-blue-500 text-6xl mx-auto mb-6 animate-spin" />
                <h1 className="text-2xl font-bold text-gray-800 mb-2">Verifying Payment...</h1>
                <p className="text-gray-600 mb-8">{message}</p>
            </>
        )}

        {status === 'success' && (
            <>
                <FaCheckCircle className="text-green-500 text-6xl mx-auto mb-6" />
                <h1 className="text-3xl font-bold text-gray-800 mb-2">Payment Successful!</h1>
                <p className="text-gray-600 mb-8">{message}</p>
                <div className="space-y-3">
                  <Link 
                    to="/account/orders" 
                    className="block w-full bg-primary hover:bg-primary-dark text-white font-bold py-3 px-4 rounded-lg transition duration-300"
                  >
                    View Order Status
                  </Link>
                  <Link 
                    to="/" 
                    className="block w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-3 px-4 rounded-lg transition duration-300 flex items-center justify-center gap-2"
                  >
                    Back to Home <FaArrowRight />
                  </Link>
                </div>
            </>
        )}

        {status === 'error' && (
            <>
                <FaExclamationTriangle className="text-red-500 text-6xl mx-auto mb-6" />
                <h1 className="text-2xl font-bold text-gray-800 mb-2">Verification Issue</h1>
                <p className="text-gray-600 mb-8">{message}</p>
                <Link 
                    to="/account" 
                    className="block w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-3 px-4 rounded-lg transition duration-300"
                >
                    Go to Dashboard
                </Link>
            </>
        )}

      </div>
    </div>
  );
}

export default PaymentSuccessPage;